import { i18n } from '@/services/TranslationService'
import { useOAuthService } from '@/services/OAuthService'
import axios from './axiosWrapper'
import { loadConfig } from '@/configLoader'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import { Capacitor } from '@capacitor/core'
import { getFirestore, doc, deleteDoc } from 'firebase/firestore'
import { clearFirebaseApp } from '@/FirebaseMessagingService'
import lodash from 'lodash'
import * as Flatted from 'flatted'
import { storeBlob, retrieveBlob, getStoredItem, removeAllStoredItems, removeStoredItem, parseJwt, clearAllCookies } from '@/services/utils'
import { Device } from '@capacitor/device'
import { Preferences } from '@capacitor/preferences'
import { getCampusId as getCampusID } from './BaseController'
import { revokeActiveIdentityIfExists, resetBlueIDState } from '@/services/BlueIDService'
import Swal from '@/services/ui/dialog'

async function isWebPlatform() {
  return !Capacitor.isNativePlatform()
}

export const getBaseUrl = async () => {
  let envConfig = await loadConfig()
  return envConfig.backboneUrl
}

export const getApiKey = async () => {
  let envConfig = await loadConfig()
  return envConfig.apiKey
}

const getFileUrl = async () => {
  let envConfig = await loadConfig()
  return envConfig.fileUrl
}

const getFileApiKey = async () => {
  let envConfig = await loadConfig()
  return envConfig.fileApiKey
}

const isStaingOrQa = async () => {
  let apiKey = await getApiKey()
  return apiKey.includes("qa") || apiKey.includes("staging")
}

const getCampusId = async () => {
  let campusId = await getCampusID()
  return campusId
}

const getConfig = async () => {
  const token = await useOAuthService().getToken()
  return {
    method: "GET",
    responseType: "json",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-ps-api-key": await getApiKey()
    }
  }
}

export async function storeData(key, data) {
  if (data instanceof Blob) {
    await storeBlob(key, data)
  } else {
    const stringifiedData = Flatted.stringify(data)
    if (await isWebPlatform()) {
      localStorage.setItem(key, stringifiedData)
    } else {
      await Preferences.set({ key, value: stringifiedData })
    }
  }
}

export async function getStoredData(key) {
  if (await isWebPlatform()) {
    const item = localStorage.getItem(key)
    return item ? Flatted.parse(item) : await retrieveBlob(key)
  } else {
    const { value } = await Preferences.get({ key })
    return value ? Flatted.parse(value) : await retrieveBlob(key)
  }
}

async function clearIndexedDBData(dbName) {
  if (!window.indexedDB) {
    return Promise.resolve()
  }

  return new Promise(resolve => {
    const openRequest = indexedDB.open(dbName)

    openRequest.onerror = () => {
      console.error("Error opening database for clearing")
      resolve()
    }

    openRequest.onsuccess = event => {
      const db = event.target.result

      try {
        if (db.objectStoreNames.length === 0) {
          db.close()
          resolve()
          return
        }

        const storeNames = Array.from(db.objectStoreNames)
        const transaction = db.transaction(storeNames, "readwrite")

        storeNames.forEach(storeName => {
          try {
            transaction.objectStore(storeName).clear()
          } catch (e) {
            console.error(`Failed to clear store ${storeName}:`, e)
          }
        })

        transaction.oncomplete = () => {
          db.close()
          console.log("IndexedDB cleared successfully")
          resolve()
        }

        transaction.onerror = () => {
          db.close()
          console.error("Transaction error while clearing")
          resolve()
        }

        transaction.onabort = () => {
          db.close()
          console.error("Transaction aborted")
          resolve()
        }
      } catch (e) {
        console.error("Error during clear operation:", e)
        db.close()
        resolve()
      }
    }

    openRequest.onblocked = () => {
      console.warn("Database open blocked")
      setTimeout(() => resolve(), 1000)
    }

    openRequest.onupgradeneeded = () => {
      console.warn("Upgrade needed, closing")
      resolve()
    }
  })
}

async function performStorageCleanup() {
  try {
    const isNative = Capacitor.isNativePlatform()

    await new Promise(resolve => setTimeout(resolve, 300))

    try {
      await Preferences.clear()
    } catch (e) {
      console.error("Preferences clear failed:", e)
    }

    try {
      await removeAllStoredItems()
    } catch (e) {
      console.error("Remove all stored items failed:", e)
    }

    if (!isNative) {
      try {
        localStorage.clear()
      } catch (e) {
        console.error("localStorage clear failed:", e)
      }

      try {
        sessionStorage.clear()
      } catch (e) {
        console.error("sessionStorage clear failed:", e)
      }

      try {
        document.cookie.split(";").forEach(cookie => {
          const eqPos = cookie.indexOf("=")
          const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
        })
      } catch (e) {
        console.error("Cookie clear failed:", e)
      }
    } else {
      try {
        await clearAllCookies()
      } catch (e) {
        console.error("clearAllCookies failed:", e)
      }
    }

    if (window.indexedDB) {
      try {
        await clearIndexedDBData("myAppDatabase")
      } catch (e) {
        console.warn("IndexedDB clear failed:", e)
      }
    }
  } catch (err) {
    console.error("performStorageCleanup failed:", err)
  }
}

async function callBackendLogout() {
  try {
    const config = await getConfig()
    const baseUrl = await getBaseUrl()
    config.url = `${baseUrl}session/logout`
    config.method = "POST"
    await axios(config)
  } catch (error) {
    if (error.response?.status !== 401) {
      console.error("Backend logout error:", error)
    }
  }
}

async function handleHBCLogout() {
  try {
    const { Capacitor } = await import("@capacitor/core")

    if (Capacitor.isNativePlatform()) {
      const { InAppBrowser } = await import("@capgo/capacitor-inappbrowser")

      await InAppBrowser.openWebView({
        url: "https://members.factoryberlin.com/logout",
        showArrow: false,
        title: "Logging out..."
      })

      const scriptInterval = setInterval(() => {
        try {
          InAppBrowser.executeScript({
            code: `
              try {
                const url = window.location.href;
                const style = document.createElement('style');
                style.innerHTML = '.member-to-be { display: none !important; }';
                document.head.appendChild(style);
                if (url === 'https://members.factory.network/dashboard') {
                  window.mobileApp?.close?.();
                }
              } catch (e) {
                console.log('Injected script error:', e);
              }
            `
          })
        } catch (e) {
          console.error("Script injection failed:", e)
        }
      }, 100)

      InAppBrowser.addListener("urlChangeEvent", async info => {
        if (info.url === "https://members.factoryberlin.com/") {
          clearInterval(scriptInterval)
          try {
            await InAppBrowser.close()
          } catch (e) {
            console.error("InAppBrowser close failed:", e)
          }
          window.location.replace("/login")
        }
      })

      setTimeout(async () => {
        clearInterval(scriptInterval)
        try {
          await InAppBrowser.close()
        } catch (e) {
          console.warn("InAppBrowser already closed", e)
        }
        window.location.replace("/login")
      }, 5000)
    } else {
      const popup = window.open("https://members.factoryberlin.com/logout", "_blank", "width=400,height=300,left=100,top=100,noopener,noreferrer")

      setTimeout(() => {
        try {
          popup?.close()
        } catch (e) {
          console.warn("Popup already closed or blocked.", e)
        }
        window.location.replace("/login")
      }, 2000)
    }
  } catch (e) {
    console.error("HBC logout flow failed:", e)
    window.location.replace("/login")
  }
}
export const performLogout = async () => {
  const t = i18n.global.t

  try {
    const result = await Swal.fire({
      title: `<span style="color: #1f2937; font-size: 24px; font-weight: 600;">${t("auth.logout-title")}</span>`,
      html: `<p style="color: #6b7280; font-size: 16px; margin: 0;">${t("auth.logout-text")}</p>`,
      icon: "warning",
      iconColor: "#f59e0b",
      showCancelButton: true,
      confirmButtonText: `<span style="font-weight: 600; color: white;">${t("auth.logout-confirm")}</span>`,
      cancelButtonText: `<span style="font-weight: 600; color: white;">${t("auth.logout-cancel")}</span>`,

      // Green confirm, Red cancel
      background: "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#ef4444",

      // Animations
      showClass: {
        popup: "animate__animated animate__fadeInDown animate__faster"
      },
      hideClass: {
        popup: "animate__animated animate__fadeOutUp animate__faster"
      },

      buttonsStyling: true,
      reverseButtons: true,
      allowOutsideClick: false,
      backdrop: "rgba(0, 0, 0, 0.5)",

      didOpen: () => {
        const popup = Swal.getPopup()
        const confirmBtn = Swal.getConfirmButton()
        const cancelBtn = Swal.getCancelButton()

        popup.style.borderRadius = "20px"
        popup.style.padding = "40px"
        popup.style.boxShadow = "0 20px 60px rgba(0, 0, 0, 0.15)"

        confirmBtn.style.background = "linear-gradient(135deg, #10b981 0%, #059669 100%)"
        confirmBtn.style.borderRadius = "12px"
        confirmBtn.style.padding = "14px 36px"
        confirmBtn.style.fontSize = "15px"
        confirmBtn.style.boxShadow = "0 4px 14px rgba(16, 185, 129, 0.4)"
        confirmBtn.style.transition = "all 0.3s ease"
        confirmBtn.style.border = "none"
        confirmBtn.style.color = "white"
        confirmBtn.style.fontWeight = "600"

        confirmBtn.onmouseover = () => {
          confirmBtn.style.transform = "translateY(-2px)"
          confirmBtn.style.boxShadow = "0 6px 20px rgba(16, 185, 129, 0.5)"
          confirmBtn.style.background = "linear-gradient(135deg, #059669 0%, #047857 100%)"
        }
        confirmBtn.onmouseout = () => {
          confirmBtn.style.transform = "translateY(0)"
          confirmBtn.style.boxShadow = "0 4px 14px rgba(16, 185, 129, 0.4)"
          confirmBtn.style.background = "linear-gradient(135deg, #10b981 0%, #059669 100%)"
        }

        cancelBtn.style.background = "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
        cancelBtn.style.borderRadius = "12px"
        cancelBtn.style.padding = "14px 36px"
        cancelBtn.style.fontSize = "15px"
        cancelBtn.style.boxShadow = "0 4px 14px rgba(239, 68, 68, 0.4)"
        cancelBtn.style.transition = "all 0.3s ease"
        cancelBtn.style.border = "none"
        cancelBtn.style.color = "white"
        cancelBtn.style.fontWeight = "600"

        cancelBtn.onmouseover = () => {
          cancelBtn.style.transform = "translateY(-2px)"
          cancelBtn.style.boxShadow = "0 6px 20px rgba(239, 68, 68, 0.5)"
          cancelBtn.style.background = "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)"
        }
        cancelBtn.onmouseout = () => {
          cancelBtn.style.transform = "translateY(0)"
          cancelBtn.style.boxShadow = "0 4px 14px rgba(239, 68, 68, 0.4)"
          cancelBtn.style.background = "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
        }
      }
    })

    if (result.isConfirmed) {
      Swal.fire({
        title: `<span style="color: #1f2937; font-size: 22px; font-weight: 600;">${t("auth.logging-out")}</span>`,
        html: `<p style="color: #6b7280; font-size: 15px; margin: 0;">${t("auth.logout-processing")}</p>`,
        allowOutsideClick: false,
        showConfirmButton: false,
        background: "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
        backdrop: "rgba(0, 0, 0, 0.5)",
        didOpen: () => {
          const popup = Swal.getPopup()
          popup.style.borderRadius = "20px"
          popup.style.padding = "40px"
          popup.style.boxShadow = "0 20px 60px rgba(0, 0, 0, 0.15)"
          Swal.showLoading()

          const loader = popup.querySelector(".swal2-loader")
          if (loader) {
            loader.style.borderColor = "#10b981 transparent #10b981 transparent"
          }
        }
      })

      await callBackendLogout()
      await performStorageCleanup()

      const apiKey = await getApiKey()
      if (apiKey.includes("hbc")) {
        await handleHBCLogout()
        return
      }

      await Swal.fire({
        title: `<span style="color: #059669; font-size: 22px; font-weight: 600;">${t("auth.logout-success-title")}</span>`,
        html: `<p style="color: #6b7280; font-size: 15px; margin: 0;">${t("auth.logout-success-text")}</p>`,
        icon: "success",
        iconColor: "#10b981",
        timer: 2000,
        showConfirmButton: false,
        background: "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
        backdrop: "rgba(0, 0, 0, 0.5)",
        showClass: {
          popup: "animate__animated animate__zoomIn animate__faster"
        },
        didOpen: () => {
          const popup = Swal.getPopup()
          popup.style.borderRadius = "20px"
          popup.style.padding = "40px"
          popup.style.boxShadow = "0 20px 60px rgba(16, 185, 129, 0.2)"
        }
      })

      window.location.replace("/login")
    }
  } catch (error) {
    console.error("Logout error:", error)

    await Swal.fire({
      title: `<span style="color: #dc2626; font-size: 22px; font-weight: 600;">${t("auth.logout-error-title")}</span>`,
      html: `<p style="color: #6b7280; font-size: 15px; margin: 0;">${t("auth.logout-error-text")}</p>`,
      icon: "error",
      iconColor: "#ef4444",
      confirmButtonText: `<span style="font-weight: 600; color: white;">OK</span>`,
      confirmButtonColor: "#3b82f6",
      background: "linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)",
      backdrop: "rgba(0, 0, 0, 0.5)",
      buttonsStyling: true,
      didOpen: () => {
        const popup = Swal.getPopup()
        const confirmBtn = Swal.getConfirmButton()

        popup.style.borderRadius = "20px"
        popup.style.padding = "40px"
        popup.style.boxShadow = "0 20px 60px rgba(239, 68, 68, 0.2)"

        confirmBtn.style.background = "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
        confirmBtn.style.borderRadius = "12px"
        confirmBtn.style.padding = "14px 36px"
        confirmBtn.style.fontSize = "15px"
        confirmBtn.style.fontWeight = "600"
        confirmBtn.style.boxShadow = "0 4px 14px rgba(59, 130, 246, 0.4)"
        confirmBtn.style.transition = "all 0.3s ease"
        confirmBtn.style.border = "none"
        confirmBtn.style.color = "white"

        confirmBtn.onmouseover = () => {
          confirmBtn.style.transform = "translateY(-2px)"
          confirmBtn.style.boxShadow = "0 6px 20px rgba(59, 130, 246, 0.5)"
          confirmBtn.style.background = "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)"
        }
        confirmBtn.onmouseout = () => {
          confirmBtn.style.transform = "translateY(0)"
          confirmBtn.style.boxShadow = "0 4px 14px rgba(59, 130, 246, 0.4)"
          confirmBtn.style.background = "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
        }
      }
    })
  }
}
export const getUsers = async () => {
  const storageKey = "users"
  let users = await getStoredData(storageKey)
  if (!users) {
    const config = await getConfig()
    config.url = `${await getBaseUrl()}users`
    try {
      const response = await axios(config)
      users = response.data.items
      await storeData(storageKey, users)
    } catch (error) {
      console.error("Error fetching users:", error)
    }
  }
  return users
}

export const createUser = async data => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  const body = JSON.stringify(data)
  config.url = `${baseUrl}users`
  config.data = body
  config.method = "POST"
  try {
    const response = await axios(config)
    return response.data
  } catch (error) {
    console.error("Error:", error)
    throw error
  }
}

export const deleteUser = async userId => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  config.url = `${baseUrl}users/${userId}`
  config.method = "DELETE"
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error("Error:", error)
    throw error
  }
}

export const updateUser = async (userId, data) => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  const body = JSON.stringify(data)
  config.url = `${baseUrl}users/${userId}`
  config.data = body
  config.method = "PUT"
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error("Error:", error)
    throw error
  }
}

export const linkRoleToUser = async (userId, roles) => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  const body = JSON.stringify(roles)
  config.url = `${baseUrl}users/${userId}/roles`
  config.data = body
  config.method = "POST"
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error("Error:", error)
    throw error
  }
}

export const unlinkRolesFromUser = async (userId, roles) => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  const body = JSON.stringify(roles)
  config.url = `${baseUrl}users/${userId}/roles`
  config.data = body
  config.method = "DELETE"
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error("Error:", error)
    throw error
  }
}

export const getRoles = async () => {
  const storageKey = "roles"
  let roles = await getStoredData(storageKey)
  if (!roles) {
    const config = await getConfig()
    config.url = `${await getBaseUrl()}roles`
    try {
      const response = await axios(config)
      roles = response.data.items
      await storeData(storageKey, roles)
    } catch (error) {
      console.error("Error fetching roles:", error)
    }
  }
  return roles
}

export const clearAllStoredRoles = async () => {
  await removeStoredItem("roles")
}

export const deleteRoleById = async roleId => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  config.url = `${baseUrl}roles/${roleId}`
  config.method = "DELETE"
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error("Error:", error)
    throw error
  }
}

export const createRole = async data => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  const body = JSON.stringify(data)
  config.url = `${baseUrl}roles`
  config.data = body
  config.method = "POST"
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error("Error:", error)
    throw error
  }
}

export const updateRole = async (roleId, data) => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  const body = JSON.stringify(data)
  config.url = `${baseUrl}roles/${roleId}`
  config.data = body
  config.method = "PUT"
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error("Error:", error)
    throw error
  }
}

export const getGroups = async ({ count = 20, offset = 0, sort = "displayName|asc", filter = "groupType=Custom" } = {}) => {
  const storageKey = "groups"
  let groups = await getStoredData(storageKey)
  if (!groups) {
    const config = await getConfig()
    const baseUrl = await getBaseUrl()
    const params = new URLSearchParams({
      sort,
      filter
    })
    config.url = `${baseUrl}groups/search?${params.toString()}`
    try {
      const response = await axios(config)
      groups = response.data.items
      await storeData(storageKey, groups)
    } catch (error) {
      console.error("Error fetching groups:", error)
    }
  }
  return groups
}

export const clearAllStoredGroups = async () => {
  await removeStoredItem("groups")
}

export const deleteGroupById = async groupId => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  config.url = `${baseUrl}groups/${groupId}`
  config.method = "DELETE"
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error("Error:", error)
    throw error
  }
}

export const createGroup = async data => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  const body = JSON.stringify(data)
  config.url = `${baseUrl}groups`
  config.data = body
  config.method = "POST"
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error("Error:", error)
    throw error
  }
}

export const updateGroup = async (groupId, data) => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  const body = JSON.stringify(data)
  config.url = `${baseUrl}groups/${groupId}`
  config.data = body
  config.method = "PUT"
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error("Error:", error)
    throw error
  }
}

export const linkGroupToUser = async (groupsId, groupId) => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  const body = JSON.stringify({ groupId })
  config.url = `${baseUrl}groups/${groupsId}/users`
  config.data = body
  config.method = "POST"
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error("Error:", error)
    throw error
  }
}

export const unlinkUserFromGroup = async (groupsId, userId) => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  const body = JSON.stringify({ groupsId })
  config.url = `${baseUrl}users/${userId}/groups`
  config.data = body
  config.method = "DELETE"
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error("Error:", error)
    throw error
  }
}

export const getLocks = async () => {
  const config = await getConfig()
  let baseUrl = await getBaseUrl()
  let campusId = await getCampusId()
  let customBase = "locks"
  customBase = baseUrl + customBase
  config["url"] = customBase
  try {
    const response = await axios(config)
    const locks = response.data.items.filter(
      lock => lock?.configs?.["blue-id-object-id"] && lock["location"]?.position && lock["location"]?.floorId && lock["location"]["campusId"] === campusId && lock["serialNumber"]
    )
    return locks
  } catch (error) {
    console.error("Error fetching locks:", error)
  }
}

export const hardwareSendCommand = async (hardwareId, data) => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  const body = JSON.stringify(data)
  config.url = `${baseUrl}hardwares/${hardwareId}/command`
  config.data = body
  config.method = "POST"
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error("Error:", error)
    throw error
  }
}

export const getHardwareByType = async (buildingId, type, forceRefresh = false) => {
  const storageKey = `hardware'${buildingId}'${type}`
  let hardwareData = forceRefresh ? null : await getStoredData(storageKey)
  if (!hardwareData) {
    const config = await getConfig()
    config.url = `${await getBaseUrl()}buildings/${buildingId}/hardwares/by-type?type=${type}`
    try {
      const response = await axios(config)
      hardwareData = response.data.items
      await storeData(storageKey, hardwareData)
    } catch (error) {
      console.error('Error fetching hardware:', error)
      // Offline on a forced refresh: the last stored list beats nothing.
      hardwareData = await getStoredData(storageKey)
    }
  }
  return hardwareData
}

export const unregisterDeviceAccessControl = async deviceId => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  const body = JSON.stringify({ deviceId })
  config['url'] = `${baseUrl}access-control/blueid/device-registration`
  config['data'] = body
  config['method'] = 'DELETE'
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error('Error:', error)
    throw error
  }
}

export const syncAccessControlDeviceId = async deviceId => {
  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  const body = JSON.stringify({ deviceId })
  config['url'] = `${baseUrl}access-control/blueid/device-registration`
  config['data'] = body
  config['method'] = 'POST'
  try {
    const response = await axios(config)
    return response
  } catch (error) {
    console.error('Error:', error)
    throw error
  }
}

export const doLogout = async () => {
  let isHBC = false
  let isMicrosoftUser = false
  let rawToken

  try {
    const oAuthService = useOAuthService()
    rawToken = await oAuthService.getToken()
    if (rawToken) {
      const parsed = parseJwt(rawToken)
      const iss = String(parsed?.iss || '').toLowerCase()
      // Only treat as Microsoft SSO if issued directly by Azure AD / Microsoft
      if (iss.includes('windows.net') || iss.includes('login.microsoftonline.com') || iss.includes('microsoft')) {
        isMicrosoftUser = true
      }
    }
  } catch (e) {
    console.warn('Could not inspect token before logout:', e)
  }

  try {
    const { getCustomer } = await import('@/assets/Branding/branding.js')
    const cust = await getCustomer()
    isHBC = cust ? cust.toLowerCase().includes('hammerbrooklyn') : false
  } catch (e) {
    console.warn('Could not determine customer:', e)
  }

  try {
    Swal.fire({
      title: 'Logging Out...',
      text: 'Please wait while we log you out.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading()
      },
    })

    // 1. Clean up BlueID identity and backend profile tag BEFORE session/logout
    // This requires a valid auth token to fetch current user profile and delete/update tags.
    if (Capacitor.isNativePlatform()) {
      try {
        await revokeActiveIdentityIfExists()
      } catch (revokeErr) {
        console.warn('⚠️ Pre-logout BlueID revoke warning:', revokeErr)
      }
    }

    // 2. Invalidate server session
    try {
      const config = await getConfig()
      const baseUrl = await getBaseUrl()
      config.url = `${baseUrl}session/logout`
      config.method = 'POST'
      await axios(config)
    } catch (apiErr) {
      console.warn('Session logout API response (proceeding with cleanup):', apiErr?.response?.status || apiErr?.message)
    }
  } catch (error) {
    console.warn('Logout flow error:', error)
  } finally {
    try {
      const { getCustomer } = await import('@/assets/Branding/branding.js')
      isHBC = (await getCustomer()).toLowerCase().includes('hammerbrooklyn')

      // Ensure local BlueID state and cryptographic data are wiped
      await resetBlueIDState({ clearSdkData: true }).catch(() => {})
      await cleanUpFirebaseFCM().catch(() => {})
      await performFullCleanup()
    } catch (e) {
      console.error('Cleanup failed:', e)
    }
  }

  if (Swal.isVisible()) Swal.close()

  const { Capacitor } = await import('@capacitor/core')

  // 5. Always clear InAppBrowser cookies & cache on native
  if (Capacitor.isNativePlatform()) {
    try {
      const { InAppBrowser } = await import('@capgo/capacitor-inappbrowser')
      await InAppBrowser.removeAllListeners().catch(() => {})
      await InAppBrowser.clearAllCookies().catch(() => {})
      await InAppBrowser.clearCache().catch(() => {})
      await InAppBrowser.close().catch(() => {})
    } catch (e) {
      console.warn('InAppBrowser cookie/cache clear error:', e)
    }
  }

  const goToLogin = async () => {
    try {
      const { default: router } = await import('@/router')
      await router.replace({ name: 'Login' }).catch(async () => {
        await router.push({ name: 'Login' }).catch(() => {
          window.location.href = '/login'
        })
      })
    } catch (e) {
      console.warn('Router redirect warning, using window.location:', e)
      window.location.href = '/login'
    }
  }

  // 6. Handle provider-specific SSO signout if applicable
  if (isHBC) {
    const hbcLogoutUrl = 'https://members.factoryberlin.com/logout'
    if (Capacitor.isNativePlatform()) {
      try {
        const { InAppBrowser } = await import('@capgo/capacitor-inappbrowser')
        await InAppBrowser.openWebView({
          url: hbcLogoutUrl,
          showArrow: false,
          title: 'Logging out...',
        })
        InAppBrowser.addListener('urlChangeEvent', async info => {
          if (info.url === 'https://members.factoryberlin.com/' || info.url?.includes('login')) {
            await InAppBrowser.removeAllListeners().catch(() => {})
            await InAppBrowser.close().catch(() => {})
            await goToLogin()
          }
        })

        setTimeout(async () => {
          await InAppBrowser.removeAllListeners().catch(() => {})
          await InAppBrowser.close().catch(() => {})
          await goToLogin()
        }, 1500)
        return
      } catch (e) {
        console.warn('HBC InAppBrowser logout error:', e)
      }
    } else {
      const popup = window.open(hbcLogoutUrl, '_blank', 'width=400,height=300,left=100,top=100,noopener,noreferrer')
      setTimeout(async () => {
        try {
          popup?.close()
        } catch (e) {
          console.warn('Popup close warning:', e)
        }
        await goToLogin()
      }, 1500)
      return
    }
  } else if (isMicrosoftUser) {
    const msLogoutUrl = 'https://login.microsoftonline.com/common/oauth2/v2.0/logout'
    if (Capacitor.isNativePlatform()) {
      try {
        const { InAppBrowser } = await import('@capgo/capacitor-inappbrowser')
        await InAppBrowser.openWebView({
          url: msLogoutUrl,
          showArrow: false,
          title: 'Logging out from Microsoft...',
        })
        InAppBrowser.addListener('urlChangeEvent', async info => {
          if (info.url?.includes('logout') || info.url?.includes('login.microsoftonline.com')) {
            setTimeout(async () => {
              await InAppBrowser.removeAllListeners().catch(() => {})
              await InAppBrowser.close().catch(() => {})
              await goToLogin()
            }, 800)
          }
        })
        setTimeout(async () => {
          await InAppBrowser.removeAllListeners().catch(() => {})
          await InAppBrowser.close().catch(() => {})
          await goToLogin()
        }, 1500)
        return
      } catch (e) {
        console.warn('Microsoft InAppBrowser logout error:', e)
      }
    } else {
      const popup = window.open(msLogoutUrl, '_blank', 'width=400,height=300,left=100,top=100,noopener,noreferrer')
      setTimeout(async () => {
        try {
          popup?.close()
        } catch (e) {
          console.warn('Popup close warning:', e)
        }
        await goToLogin()
      }, 1500)
      return
    }
  }

  // 7. Default redirect for Keycloak / Staging auth
  await goToLogin()
}

const cleanUpFirebaseFCM = async () => {
  try {
    const oAuthService = useOAuthService()
    const isDev = window.location.hostname.includes('localhost') || window.location.hostname.startsWith('192.168.')
    const topic = isDev ? 'news-dev' : 'news'
    const platform = Capacitor.getPlatform()

    const isLoggedIn = oAuthService.isLoggedIn()
    let SUB_ID = null
    let EMAIL = null

    if (isLoggedIn) {
      const USERPROFILE = await oAuthService.getUserProfile()
      SUB_ID = USERPROFILE?.sub
      EMAIL = USERPROFILE?.email
    }

    const db = getFirestore()
    const { identifier: deviceId } = await Device.getId()

    // 🗑️ Remove Firestore token
    if (SUB_ID && deviceId) {
      try {
        await deleteDoc(doc(db, `userTokens/${SUB_ID}/devices/${deviceId}`))
        console.log(`🗑️ Firestore token deleted for ${SUB_ID}/${deviceId}`)
      } catch (err) {
        console.warn('⚠️ Failed to delete Firestore token:', err)
      }
    }

    // 🔄 Native
    if (platform === 'android' || platform === 'ios') {
      try {
        const { token } = await FirebaseMessaging.getToken()
        if (token) {
          await FirebaseMessaging.unsubscribeFromTopic({ topic })
          await FirebaseMessaging.deleteToken()
          console.log('📴 Native FCM token unsubscribed and deleted')
        }
      } catch (err) {
        console.warn('⚠️ Native FCM cleanup error:', err)
      }
    }

    // 🌐 Web
    if (platform === 'web') {
      try {
        const token = await getStoredItem('fcm_token')
        if (token) {
          console.log('📴 Web FCM token removed from storage')
        }
      } catch (err) {
        console.warn('⚠️ Web FCM cleanup error:', err)
      }
    }

    // 🗂 Remove local token refs in any case
    await removeStoredItem('fcm_token')
    await removeStoredItem('fcm_sub_id')
    await clearFirebaseApp()
  } catch (err) {
    console.error('🚫 cleanUpFirebaseFCM failed:', err)
  }
}

export const performFullCleanup = async () => {
  try {
    const oAuthService = useOAuthService()

    // Small wait to ensure Firebase cleanup finishes
    await new Promise(resolve => setTimeout(resolve, 300))

    await Preferences.clear()
    await removeAllStoredItems()
    localStorage.clear()
    sessionStorage.clear()

    // 🍪 Clear all cookies
    document.cookie.split(';').forEach(cookie => {
      const eqPos = cookie.indexOf('=')
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
    })

    // 🧱 Clear IndexedDB
    if (window.indexedDB) {
      try {
        await new Promise((resolve, reject) => {
          const request = window.indexedDB.deleteDatabase('myAppDatabase')
          request.onsuccess = () => resolve()
          request.onerror = () => reject(request.error)
          request.onblocked = () => resolve()
        })
      } catch (e) {
        console.warn('⚠️ Failed to clear IndexedDB:', e)
      }
    }

    await oAuthService.performLogout()
  } catch (err) {
    console.error('🚫 performFullCleanup failed:', err)
  }
}

export const getElevators = async buildingId => {
  const storageKey = `elevatorsData_${buildingId}`
  let elevatorsData = await getStoredData(storageKey)
  if (!elevatorsData) {
    const config = await getConfig()
    config.url = `${await getBaseUrl()}buildings/${buildingId}/elevators`
    try {
      let response = await axios(config)
      elevatorsData = response.data.items
      console.log(elevatorsData)
      await storeData(storageKey, elevatorsData)
    } catch (error) {
      console.error("Error fetching elevators:", error)
      throw error
    }
  }
  return elevatorsData
}

export const callElevator = async (elevatorId, entranceId, destinationLevel) => {
  try {
    const config = await getConfig()
    const baseUrl = await getBaseUrl()
    config.url = `${baseUrl}elevators/${elevatorId}/call`
    const body = {
      entranceId,
      destinationLevel
    }
    config.method = "POST"
    config.data = body
    const response = await axios(config)
    return response.data
  } catch (error) {
    console.error("Error calling elevator:", error)
    throw error
  }
}

export async function registerIdentityWithBackend(deviceId, forceNew = false) {
  if (!forceNew) {
    const cached = await getStoredData('blueid_identity')
    if (cached?.identityId && cached?.activationToken) {
      console.log('✅ BlueID identity already registered:', cached)
      return cached
    }
  }

  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  config.url = `${baseUrl}access-control/blueid/v2/device-registration`
  config.method = "POST"
  config.data = { deviceId }

  try {
    const response = await axios(config)
    const data = response.data

    if (!data?.identityId || !data?.activationToken) {
      throw new Error("Missing identityId or activationToken in backend response")
    }

    await storeData("blueid_identity", data)
    console.log("BlueID identity registered successfully:", data)
    return data
  } catch (error) {
    console.error("Error registering BlueID identity:", error)
    throw error
  }
}

export async function notifyBackendClaimed(identityId) {
  console.log("Notifying backend that identity is claimed:", identityId)

  const alreadyNotified = await getStoredData("blueid_claimed_identity_notified")
  if (alreadyNotified?._id === identityId) {
    console.log("Already notified backend:", alreadyNotified)
    return alreadyNotified
  }

  const config = await getConfig()
  const baseUrl = await getBaseUrl()
  config.url = `${baseUrl}access-control/blueid/v2/identities/${identityId}/claimed`
  config.method = "PUT"

  try {
    const response = await axios(config)
    const resultArray = response.data

    const matched = resultArray.find(item => item?._id === identityId)
    if (!matched) {
      throw new Error("Invalid response: missing or mismatched identity data")
    }

    await storeData("blueid_claimed_identity_notified", matched)
    console.log("Backend notified of claim:", matched)
    return matched
  } catch (error) {
    console.error("Error notifying backend of claimed identity:", error)
    throw error
  }
}

export const clearBlueIDIdentity = async () => {
  console.log("Clearing BlueID identity from local storage")
  await removeStoredItem("blueid_identity")
  await removeStoredItem("blueid_claimed_identity")
  await removeStoredItem("blueid_claimed_identity_notified")
  console.log("BlueID identity cleared successfully")
}
